import React, { useState } from 'react'
import { useQuery } from 'react-query'
import {
  StateMachineProvider,
  createStore,
  setStorageType,
} from 'little-state-machine'
import ReactSelect from 'react-select'
import styled from 'styled-components'

import Button from '../../components/Button'
import { ID } from '../../types/id'
import FormField from '../../components/FormField/index'
import { useWithAuth } from '../App'
import { inquiryStyles } from '../../components/Select/styles'
import { TeleportToAppSidePanel } from '../../components/SidePanel/SidePanel'
import { Panel } from '../../components/Panel/Panel'

import ReportsSingleForm from './ReportsSingleForm'
import ReportsAggregateForm from './ReportsAggregateForm'
import ReportsScalesForm from './ReportsScalesForm'
import { reportType, PatientReportData, tasks } from './utils'
import { initialState } from '../../utils/initialState'
import WithFocus from '../../components/WithFocus'

type Props = {
  inquired: boolean
  patientFirstName: string
  patientLastName: string
  code: string
  diagnosisStatus: string
  id: ID
  onClose: () => void
}

setStorageType({
  setItem: () => {},
  getItem: () => {
    return initialState
  },
  ...(typeof window !== 'undefined' && { ...localStorage }),
})
createStore(initialState)

const ReportsForm = ({
  id,
  patientFirstName,
  patientLastName,
  onClose,
  diagnosisStatus,
  inquired,
  code,
}: Props) => {
  const [activeType, setActiveType] = useState<string | null>(null)
  const [activeTask, setActiveTask] = useState(null)
  const handleActiveTypeState = (option) => {
    setActiveType(option)
    setActiveTask(null)
  }
  const filteredReportType = () => {
    if (inquired && diagnosisStatus === 'DONE') return reportType
    else if (inquired && diagnosisStatus !== 'DONE')
      return reportType.filter(
        (type) => type.value === 'inquiriesReport' && type
      )
    else if (!inquired && diagnosisStatus === 'DONE')
      return reportType.filter(
        (type) => type.value !== 'inquiriesReport' && type
      )
  }
  const withAuth = useWithAuth()
  const fetchPatientData = () => {
    if (diagnosisStatus === 'DONE') {
      const { data: patientData } = useQuery<PatientReportData, string>(
        `patientData-${id}`,
        () =>
          withAuth(
            `/results/rest/results/examination/${id}`,
            {
              method: 'GET',
            },
            true
          ),
        {
          retry: false,
          refetchOnWindowFocus: false,
        }
      )
      return patientData
    } else return null
  }

  const last2 = code.slice(-2)

  const filteredTasks = tasks
    .map((task) => {
      if (last2 === '05') {
        let newLabel = task.label

        if (
          task.label ===
          'Przełączanie - elastyczność poznawcza. (Słońce i deszcz)'
        ) {
          newLabel =
            'Przełączanie - elastyczność poznawcza_słuchowy ("Słońce i deszcz")'
        }

        if (task.label === 'Przysłowia') {
          newLabel = 'Przysłowia_słuchowy'
        }
        if (
          task.label ===
          'Zakres operacyjny - pamięć robocza ("Zapamiętywanie zwierząt", "Zapamiętywanie liter")'
        ) {
          newLabel =
            'Zakres operacyjny – pamięć robocza_słuchowy ("Zapamiętywanie zwierząt", "Zapamiętywanie liter")'
        }

        return {
          value: task.value,
          label: newLabel,
        }
      }
      return task
    })
    .filter((task) =>
      fetchPatientData()?.tests.find((test) => task.value === test.code)
    )

  return (
    <StateMachineProvider>
      <TeleportToAppSidePanel>
        <Panel allowOverflow>
          <WithFocus>
            <h1>Generowanie Raportu</h1>
            <SelectGrid>
              <FormField label="Dodaj">
                <StyledReactSelect
                  onChange={(option: string) =>
                    handleActiveTypeState(option['value'])
                  }
                  options={filteredReportType()}
                  aria-label
                  styles={inquiryStyles}
                  placeholder="Wybierz"
                ></StyledReactSelect>
              </FormField>
              {activeType === 'singleReport' && (
                <FormField label="Zadanie">
                  <StyledReactSelect
                    onChange={(option: string) =>
                      setActiveTask(option['value'])
                    }
                    options={filteredTasks}
                    aria-label
                    styles={inquiryStyles}
                    placeholder="Wybierz"
                    noOptionsMessage={() => 'Brak przeprowadzonych badań'}
                  ></StyledReactSelect>
                </FormField>
              )}
            </SelectGrid>
            {(!activeType ||
              (activeType === 'singleReport' && !activeTask)) && (
              <StyledButton color="white" label="Anuluj" onClick={onClose} />
            )}
            {(activeType === 'singleReport' && activeTask !== null && (
              <ReportsSingleForm
                id={id}
                patientFirstName={patientFirstName}
                patientLastName={patientLastName}
                reportType={activeType}
                task={activeTask}
                onClose={onClose}
              />
            )) ||
              (activeType === 'aggregateReport' && (
                <ReportsAggregateForm
                  patientFirstName={patientFirstName}
                  patientLastName={patientLastName}
                  onClose={onClose}
                  id={id}
                  reportType={activeType}
                />
              )) ||
              (activeType === 'inquiriesReport' && (
                <ReportsScalesForm
                  patientFirstName={patientFirstName}
                  patientLastName={patientLastName}
                  id={id}
                  code={code}
                  reportType={activeType}
                  onClose={onClose}
                />
              ))}
          </WithFocus>
        </Panel>
      </TeleportToAppSidePanel>
    </StateMachineProvider>
  )
}
const SelectGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  width: 35vw;
  min-width: 100%;
  margin-bottom: 2em;
`
const StyledReactSelect = styled(ReactSelect)`
  width: 95%;
`

const StyledButton = styled(Button)`
  margin-top: 5%;
  width: 47.5%;
`

export default ReportsForm
